import React, { FC } from 'react';
import { RegisterUserMutationVariables } from '../../API';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { UsagePolicyEnglish, UsagePolicyGerman } from './UsagePolicyContent';
import {
  DataProtectionPolicyEnglish,
  DataProtectionPolicyGerman,
} from './DataProtectionPolicyContent';
import Divider from '@mui/material/Divider';

export const RegistrationSubmission: FC<{
  name: string;
  currentData: Partial<RegisterUserMutationVariables>;
  back?: (dataUpdate?: Partial<RegisterUserMutationVariables>) => void;
  next: (
    updateData: Partial<RegisterUserMutationVariables>,
    done: boolean,
  ) => void;
}> = ({ next, currentData, back }) => {
  const [confirmedUsagePolicy, setConfirmedUsagePolicy] = React.useState(
    currentData.confirmedUsagePolicy ?? false,
  );
  const [confirmedDataProtectionPolicy, setConfirmedDataProtectionPolicy] =
    React.useState(currentData.confirmedUsagePolicy ?? false);
  const [usagePolicyLanguage, setUsagePolicyLanguage] = React.useState<
    'de' | 'en'
  >('de');
  const [dataProtectionPolicyLanguage, setDataProtectionPolicyLanguage] =
    React.useState<'de' | 'en'>('de');

  console.log(currentData);
  return (
    <Stack spacing={2}>
      <Typography variant="subtitle1">Complete registration</Typography>
      <Typography variant="body2">
        {usagePolicyLanguage === 'en' && (
          <>
            The following{' '}
            <a href={'https://osf.io/b2g7f'} target={'_blank'}>
              terms of use
            </a>{' '}
            must be read and accepted before the application can be sent.
          </>
        )}
        {usagePolicyLanguage === 'de' && (
          <>
            Die folgenden{' '}
            <a href={'https://osf.io/m832c'} target={'_blank'}>
              AGBs
            </a>{' '}
            müssen gelesen und akzeptiert werden, bevor die Bewerbung gesendet
            werden kann.
          </>
        )}
      </Typography>
      <Tabs
        value={usagePolicyLanguage}
        onChange={(_, val) => setUsagePolicyLanguage(val)}
      >
        <Tab value="de" label="Deutsch" />
        <Tab value="en" label="English" />
      </Tabs>
      <Box
        sx={{
          margin: '1em',
          border: '1px solid black',
          padding: '1em',
          maxHeight: '40vh',
          overflow: 'auto',
          backdropFilter: 'brightness(1.5)',
        }}
      >
        {usagePolicyLanguage === 'de' && (
          <UsagePolicyGerman currentData={currentData} />
        )}
        {usagePolicyLanguage === 'en' && (
          <UsagePolicyEnglish currentData={currentData} />
        )}
      </Box>
      <FormControlLabel
        control={
          <Checkbox
            checked={confirmedUsagePolicy}
            onChange={() => setConfirmedUsagePolicy((c) => !c)}
          />
        }
        label={
          <Typography variant="inherit">
            {usagePolicyLanguage === 'de' && (
              <>
                Ich bestätige hiermit, dass ich die vorstehenden Allgemeinen
                Geschäftsbedingungen für die Nutzung der MIND.set-Plattform
                gelesen habe. Ich, {currentData.name}, stimme der
                Nutzungsvereinbarung des Deutschen Zentrums für Integrations-
                und Migrationsforschung (DeZIM) e.V. am{' '}
                {new Date().toLocaleDateString('de-DE', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                })}{' '}
                zu.
              </>
            )}
            {usagePolicyLanguage === 'en' && (
              <>
                I hereby confirm that I have read the above General Terms and
                Conditions for the use of the MIND.set platform. I,{' '}
                {currentData.name}, agree to the user agreement of the German
                Center for Integration and Migration Research (DeZIM) e.V. at{' '}
                {new Date().toLocaleDateString('de-DE', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                })}{' '}
              </>
            )}
          </Typography>
        }
      />
      <Divider sx={{ py: '1em' }} />
      <Typography variant="body2">
        {dataProtectionPolicyLanguage === 'en' && (
          <>
            The following{' '}
            <a href={'https://osf.io/nezkf'} target={'_blank'}>
              data protection policy
            </a>{' '}
            must be read and accepted before the application can be sent.
          </>
        )}
        {dataProtectionPolicyLanguage === 'de' && (
          <>
            Die folgende{' '}
            <a href={'https://osf.io/bxm8z'} target={'_blank'}>
              Datenschutzerklärung{' '}
            </a>{' '}
            muss gelesen und akzeptiert werden, bevor die Bewerbung gesendet
            werden kann.
          </>
        )}
      </Typography>
      <Tabs
        value={dataProtectionPolicyLanguage}
        onChange={(_, val) => setDataProtectionPolicyLanguage(val)}
      >
        <Tab value="de" label="Deutsch" />
        <Tab value="en" label="English" />
      </Tabs>
      <Box
        sx={{
          margin: '1em',
          border: '1px solid black',
          padding: '1em',
          maxHeight: '40vh',
          overflow: 'auto',
          backdropFilter: 'brightness(1.5)',
        }}
      >
        {dataProtectionPolicyLanguage === 'de' && (
          <DataProtectionPolicyGerman currentData={currentData} />
        )}
        {dataProtectionPolicyLanguage === 'en' && (
          <DataProtectionPolicyEnglish currentData={currentData} />
        )}
      </Box>
      <Box>
        {dataProtectionPolicyLanguage === 'de' && (
          <>
            Bitte nehmen Sie das{' '}
            <a href={'https://osf.io/kau3m'} target="_blank">
              Muster der Datenschutzinformationen und Einwilligungserklärung für
              Studienteilnehmende – Anhang
            </a>{' '}
            zur Kenntnis bevor Sie Teilnehmende mit MIND.set befragen.
          </>
        )}
        {dataProtectionPolicyLanguage === 'en' && (
          <>
            Please note the{' '}
            <a href={'https://osf.io/e6bg7'} target="_blank">
              Appendix: Sample data protection information and declaration of
              consent for study participant
            </a>{' '}
            before interviewing participants with MIND.set.
          </>
        )}
      </Box>
      <FormControlLabel
        control={
          <Checkbox
            checked={confirmedDataProtectionPolicy}
            onChange={() => setConfirmedDataProtectionPolicy((c) => !c)}
          />
        }
        label={
          <Typography variant="inherit">
            {dataProtectionPolicyLanguage === 'de' && (
              <>
                Ich bestätige hiermit, dass ich die vorstehende Vereinbarung
                über Teilnehmendeninformation und Datenschutz (TID) für die
                Nutzung der MIND.set-Plattform gelesen habe. Ich,{' '}
                {currentData.name}, stimme dieser Vereinbarung des Deutschen
                Zentrums für Integrations- und Migrationsforschung (DeZIM) e.V.
                am{' '}
                {new Date().toLocaleDateString('de-DE', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                })}{' '}
                zu.
              </>
            )}
            {dataProtectionPolicyLanguage === 'en' && (
              <>
                I hereby confirm that I have read the above agreement on
                participant information and data protection (TID) for the use of
                the MIND.set platform. I, {currentData.name}, agree to this
                agreement of the German Center for Integration and Migration
                Research (DeZIM) e.V. on{' '}
                {new Date().toLocaleDateString('de-DE', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                })}
                .
              </>
            )}
          </Typography>
        }
      />
      <Stack
        direction="row"
        sx={{
          width: '100%',
          justifyContent: 'space-between',
          marginTop: '1.5rem',
        }}
      >
        <Button
          onClick={() => {
            back?.({
              confirmedUsagePolicy:
                confirmedDataProtectionPolicy && confirmedUsagePolicy,
            });
          }}
        >
          Back
        </Button>
        <Button
          disabled={!confirmedDataProtectionPolicy || !confirmedUsagePolicy}
          onClick={() => {
            next(
              {
                confirmedUsagePolicy:
                  confirmedDataProtectionPolicy && confirmedUsagePolicy,
              },
              true,
            );
          }}
        >
          Submit
        </Button>
      </Stack>
    </Stack>
  );
};
